/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {Directive, ElementRef, HostBinding, HostListener, Input, Renderer2} from '@angular/core';

import {ModalService} from './modal.service';


/**
 * Indicates that a node acts as a possible modal content with a modal identifier
 *
 * Usage:
 *
 * ```
 * <div gp-modal='confirm'>
 *     Are you really sure ?
 * </div>
 * ```
 */
@Directive({
  selector: '[gp-modal]'
})
export class ModalDirective {

  @Input('gp-modal')
  public modalId: string;

  @Input('gp-modal-closable')
  public isClosable: boolean;

  @HostBinding('style.display') get getStyle() {
    return this.isOpen ? 'block' : 'none';
  }

  @HostBinding('class.in') get getIn() {
    return this.isOpen;
  }

  private isOpen: boolean = false;

  constructor(
    private modalService: ModalService,
    private renderer: Renderer2,
    private elementRef: ElementRef
  ) {
    modalService.modalOpened.subscribe((id: string) => {
      if (id === this.modalId) {
        this.renderer.addClass(document.body, 'modal-open');
        this.isOpen = true;
      }
    });

    modalService.modalClosed.subscribe((id: string) => {
      if (id === this.modalId && !this.modalService.modalWithIdOpen.length) {
        this.isOpen = false;
        this.renderer.removeClass(document.body, 'modal-open');
      }
    });
  }

  @HostListener('click', ['$event.target'])
  public onClick(target: HTMLElement) {
    if (this.isClosable && this.elementRef.nativeElement === target) {
      this.isOpen = false;
      this.modalService.modalClosed.emit(this.modalId);
    }
  }

}

/**
 * Directives that allows to have any DOM element act as a close button for the topmost open modal.
 */
@Directive({
  selector: '[gp-modal-close]'
})
export class ModalCloseDirective {

  @Input('gp-modal-close')
  public modalId: string;

  constructor(private modalService: ModalService) {
  }

  @HostListener('click', ['$event.target'])
  public onClick() {
    this.modalService.modalClosed.emit(this.modalId);
  }
}
